import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';

// Used for both post-activation welcome (/welcome?token=...) and password reset (/reset-password?token=...)
export default function WelcomePage({ mode = 'welcome', onAuth }) {
  const navigate = useNavigate();
  const [status, setStatus]     = useState('loading'); // loading | ready | error | done
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const token = new URLSearchParams(window.location.search).get('token');

  useEffect(() => {
    if (!token) { setStatus('error'); setError('Missing token.'); return; }
    const endpoint = mode === 'welcome'
      ? `${API}/api/auth/welcome-check?token=${encodeURIComponent(token)}`
      : `${API}/api/auth/reset-check?token=${encodeURIComponent(token)}`;
    fetch(endpoint)
      .then(r => r.json())
      .then(data => {
        if (data.error) { setStatus('error'); setError(data.error); }
        else { setEmail(data.email); setStatus('ready'); }
      })
      .catch(() => { setStatus('error'); setError('Network error — please try again.'); });
  }, [token, mode]);

  async function submit(e) {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 8)  { setError('Password must be at least 8 characters.'); return; }
    setError(''); setLoading(true);
    try {
      const endpoint = mode === 'welcome'
        ? `${API}/api/auth/set-password`
        : `${API}/api/auth/reset-password`;
      const res  = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Something went wrong'); setLoading(false); return; }
      // Auto-login
      localStorage.setItem('ic_token', data.token);
      localStorage.setItem('ic_user', JSON.stringify(data.user));
      setStatus('done');
      setTimeout(() => {
        onAuth(data.user, data.token);
        navigate('/', { replace: true });
      }, 800);
    } catch { setError('Network error — please try again'); }
    setLoading(false);
  }

  return (
    <div style={{
      width: '100vw', height: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)',
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          width: '100%', maxWidth: 400,
          background: 'var(--card-bg)',
          border: '1px solid var(--border)',
          borderRadius: 20,
          padding: '40px 36px',
          boxShadow: 'var(--card-shadow)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/ideacanvas-logo.png" alt="IdeaCanvas" style={{ width: 120, marginBottom: 12 }} />
        </div>

        {/* Loading */}
        {status === 'loading' && (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
            <motion.div animate={{ opacity: [0.4, 1, 0.4] }} transition={{ repeat: Infinity, duration: 1.2 }}>
              Verifying link…
            </motion.div>
          </div>
        )}

        {/* Invalid token */}
        {status === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>❌</div>
            <h2 style={{ margin: '0 0 10px', color: '#ef4444', fontSize: 18 }}>Link expired</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.6, margin: '0 0 24px' }}>
              {error || 'This link is invalid or has expired.'}
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              {mode === 'reset'
                ? 'Go back to sign in and request a new reset link.'
                : 'Contact support if you need a new activation link.'}
            </p>
            <a href="/" style={{ display: 'inline-block', marginTop: 20, color: '#6366f1', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
              Back to sign in
            </a>
          </div>
        )}

        {/* Done */}
        {status === 'done' && (
          <div style={{ textAlign: 'center' }}>
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              style={{ fontSize: 48, marginBottom: 14 }}
            >🎉</motion.div>
            <h2 style={{ margin: '0 0 10px', color: 'var(--text-primary)', fontSize: 18 }}>All set!</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Signing you in…</p>
          </div>
        )}

        {/* Set password form */}
        {status === 'ready' && (
          <form onSubmit={submit}>
            <h2 style={{ margin: '0 0 6px', fontSize: 20, color: 'var(--text-primary)', textAlign: 'center' }}>
              {mode === 'welcome' ? 'Create your password' : 'Choose a new password'}
            </h2>
            {email && (
              <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)', margin: '0 0 24px' }}>
                {email}
              </p>
            )}

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Password</label>
              <input
                type="password" required autoFocus
                value={password} onChange={e => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Confirm password</label>
              <input
                type="password" required
                value={confirm} onChange={e => setConfirm(e.target.value)}
                placeholder="••••••••"
                style={inputStyle}
              />
            </div>

            {error && (
              <div style={{
                marginBottom: 16, padding: '10px 14px', borderRadius: 10,
                background: '#ef444418', border: '1px solid #ef444440',
                fontSize: 13, color: '#ef4444',
              }}>{error}</div>
            )}

            <motion.button
              whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}
              type="submit" disabled={loading}
              style={{
                width: '100%', padding: '12px', borderRadius: 12, border: 'none',
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                color: 'white', fontWeight: 700, fontSize: 15,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                fontFamily: 'inherit',
              }}
            >
              {loading ? '…' : mode === 'welcome' ? 'Set password & sign in' : 'Reset password & sign in'}
            </motion.button>
          </form>
        )}
      </motion.div>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '10px 14px', borderRadius: 10,
  border: '1px solid var(--border)', background: 'var(--bg-2)',
  color: 'var(--text-primary)', fontSize: 14, fontFamily: 'inherit',
  outline: 'none', boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: 'var(--text-muted)', marginBottom: 6,
};
